import { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Quote, QuoteProspectInfo } from 'queries';
import { toEuros } from 'lib/numbers';
import { QuoteLine, lineTotal, sumLines, VAT_RATE } from './types';
import { useQuoteTranslation } from './useQuoteTranslation';
import Logo from '@app/illu/Branding/LogoBuxWoTdz.svg';

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;
const PAGE_PADDING = 48;
const PAGE_CONTENT_HEIGHT = A4_HEIGHT - PAGE_PADDING * 2;

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
  },
  pages: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: A4_WIDTH,
    height: A4_HEIGHT,
    flex: 'none',
    boxSizing: 'border-box',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.std,
    border: {
      color: theme.colors.grayBorder,
      style: 'solid',
      width: 1,
    },
    padding: PAGE_PADDING,
    overflow: 'hidden',
  },
  window: {
    height: PAGE_CONTENT_HEIGHT,
    overflow: 'hidden',
  },
  pageTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    ...theme.fonts.h5,
    marginBottom: theme.marginBase * 3,
  },
  richText: {
    ...theme.fonts.caption,
    marginBottom: theme.marginBase * 4,
    '& [data-columns]': {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gap: theme.marginBase * 6,
      marginBottom: theme.marginBase * 3,
    },
    '& [data-column]:last-child': {
      textAlign: 'right',
    },
    '& [data-block]': {
      display: 'inline-block',
      textAlign: 'left',
    },
    '& [data-column]:last-child > p, & [data-column]:last-child > ul': {
      textAlign: 'left',
    },
    '& p': {
      margin: 0,
    },
    '& li': {
      marginLeft: 40,
    },
    '& .prospect-label': {
      color: theme.colors.inkSecondary,
      marginRight: theme.marginBase,
    },
    '& p[data-align="justify"], & div[data-align="justify"], & li[data-align="justify"]':
      {
        textAlign: 'justify',
      },
  },
  footerParagraph: {
    ...theme.fonts.body,
    marginTop: theme.marginBase * 3,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'right',
    ...theme.fonts.caption,
    '& th, & td': {
      padding: theme.marginBase,
      borderBottom: {
        color: theme.colors.grayBorder,
        style: 'solid',
        width: 1,
      },
    },
  },
  left: {
    textAlign: 'left',
  },
  strong: {
    ...theme.fonts.label,
  },
}));

interface Props {
  locale: string;
  quote: Quote;
  prospect?: QuoteProspectInfo | null;
  vat: boolean;
  headerHtml?: string;
  footerHtml?: string;
  lines: QuoteLine[];
}

export const QuotePreview = ({
  locale,
  quote,
  prospect,
  vat,
  headerHtml,
  footerHtml,
  lines,
}: Props) => {
  const classes = useStyles();
  const t = useQuoteTranslation(locale);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const node = contentRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(([entry]) =>
      setContentHeight(entry.contentRect.height),
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const pageCount = Math.max(Math.ceil(contentHeight / PAGE_CONTENT_HEIGHT), 1);
  const pages = useMemo(
    () => Array.from({ length: pageCount }, (_, index) => index),
    [pageCount],
  );

  const total = sumLines(lines);
  const taxes = vat ? Math.round(total * VAT_RATE) : 0;
  const price = (amount: number) => toEuros(amount, false, locale);

  const document = (
    <>
      <div className={classes.pageTitle}>
        <img src={Logo} alt="BilendiUX Logo" />
      </div>
      {!!headerHtml && (
        <div
          className={classes.richText}
          dangerouslySetInnerHTML={{ __html: headerHtml }}
        />
      )}

      <table className={classes.table}>
        <thead>
          <tr className={classes.strong}>
            <th className={classes.left}>{t('admin.billing.table.product')}</th>
            <th>{t('admin.billing.table.price')}</th>
            <th>{t('admin.billing.table.quantity')}</th>
            <th>{t('admin.billing.table.total')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id}>
              <td className={classes.left}>{line.title}</td>
              <td>{price(line.unitPrice)}</td>
              <td>{line.quantity}</td>
              <td>{price(lineTotal(line))}</td>
            </tr>
          ))}
          <tr className={classes.strong}>
            <td className={classes.left}>{t('quote.quote.totalNoTax')}</td>
            <td />
            <td />
            <td>{price(total)}</td>
          </tr>
          {vat && (
            <tr>
              <td className={classes.left}>
                {t('quote.quote.vat', { rate: VAT_RATE * 100 })}
              </td>
              <td />
              <td />
              <td>{price(taxes)}</td>
            </tr>
          )}
          <tr className={classes.strong}>
            <td className={classes.left}>{t('quote.quote.totalWithTax')}</td>
            <td />
            <td />
            <td>{price(total + taxes)}</td>
          </tr>
        </tbody>
      </table>
      {!!footerHtml && (
        <div
          className={classnames(classes.footerParagraph, classes.richText)}
          dangerouslySetInnerHTML={{ __html: footerHtml }}
        />
      )}
    </>
  );

  return (
    <div className={classes.container}>
      <div className={classes.pages}>
        {pages.map((page) => (
          <div key={page} className={classes.page}>
            <div className={classes.window}>
              <div
                ref={page === 0 ? contentRef : undefined}
                style={{ marginTop: -page * PAGE_CONTENT_HEIGHT }}
              >
                {document}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
